import { useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Icon, Stack, Select, MenuItem, FormControl, InputLabel, Alert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

const CsvUploadButtonProduct = ({ hasLimit, buttonStyle = '' }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [variantFile, setVariantFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('product');

  const csvOptions = [
    {
      type: 'product',
      label: '商品情報の更新',
      templatePaths: [
        { path: '/product/product-csv-template', name: 'tendenec-product-csv-template.csv' }
      ],
      uploadPath: '/product/products-upload-csv',
    },
    {
      type: 'variant',
      label: '商品のバリエーション情報の更新',
      templatePaths: [
        { path: '/product/product-variant-csv-template', name: 'tendenec-product-variant-csv-template.csv' }
      ],
      uploadPath: '/product/product-variants-upload-csv',
    },
    ...(hasLimit('ec') ? [{
      type: 'spec',
      label: '商品のスペック情報の更新',
      templatePaths: [
        { path: '/product/product-spec-csv-template', name: 'tendenec-product-spec-csv-template.csv' }
      ],
      uploadPath: '/product/product-specs-upload-csv'
    }] : []),
    {
      type: 'create',
      label: '商品の新規登録',
      templatePaths: [
        { path: '/product/product-csv-template-create', name: 'tendenec-product-create-template.csv' },
        { path: '/product/product-variant-csv-template-create', name: 'tendenec-product-variant-create-template.csv' }
      ],
      uploadPath: '/product/products-with-variants-create-csv'
    }
  ];

  const handleDownloadTemplate = async (apiPath, fileName) => {
    try {
      const res = await axiosInstance.post(apiPath, {}, { responseType: 'blob' });

      const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const blob = new Blob([bom, res.data], { type: 'text/csv;charset=utf-8;' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('ダウンロードに失敗しました。', err);
    }
  };

  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
    setMessage('');
    e.target.value = '';
  };

  const handleVariantFileChange = (e) => {
    setVariantFile(e.target.files[0]);
    setMessage('');
    e.target.value = '';
  };

  const handleUpload = async () => {
    const option = csvOptions.find(o => o.type === selectedType);
    if (!option) {
      setMessage('アップロード対象が選択されていません。');
      return;
    }

    // 👇ここだけにする（一本化）
    if (selectedType === 'create') {
      if (!csvFile || !variantFile) {
        setMessage('商品CSVとバリアントCSVの両方が必要です。');
        return;
      }
    } else {
      if (!csvFile) {
        setMessage('CSVファイルを選択してください。');
        return;
      }
    }

    try {
      setIsUploading(true);

      const formData = new FormData();

      if (selectedType === 'create') {
        formData.append('productsCsv', csvFile);
        formData.append('variantsCsv', variantFile);
      } else {
        formData.append('csv', csvFile);
      }

      const res = await axiosInstance.post(option.uploadPath, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setMessage(res.data.message || 'アップロード成功');

    } catch (error) {
      console.error('CSVアップロード失敗:', error);
      const errMsg = error.response?.data?.message || error.message;
      setMessage(`エラー: ${errMsg}`);
    } finally {
      setIsUploading(false);
      setCsvFile(null);
      setVariantFile(null);
    }
  };
  const handleClose = () => {
    setIsModalOpen(false);
    setMessage('');
    setCsvFile(null);
    setVariantFile(null);
  };
  return (
    <>
      <Button
        variant="related-menu"
        onClick={() => setIsModalOpen(true)}
        className={buttonStyle}
        startIcon={<Icon sx={{ fontSize: 30 }}>upload</Icon>}
      >
        商品CSV登録
      </Button>
      <Dialog
        open={isModalOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>CSVアップロード</DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={3}>

            <Typography>
              商品・バリエーション・スペックのCSV登録ができます。
            </Typography>

            {selectedType === 'product' && (
              <>
                <Alert severity="info">
                  既存商品を更新する場合に使用してください。
                </Alert>
                <Alert severity="warning">
                  <strong>product_id</strong>カラムは変更しないでください。
                </Alert>
              </>
            )}

            {selectedType === 'variant' && (
              <>
                <Alert severity="info">
                  既存商品のバリエーションを追加・更新する場合に使用してください。
                </Alert>
                <Alert severity="warning">
                  <strong>variant_id</strong>カラムは変更しないでください。<br />
                  既存商品へのバリエーション追加は<strong>variant_id</strong>を空にすることで追加できます。<br />
                  <strong>product_code</strong>カラムに既存の商品コードを入力してください。
                </Alert>
              </>
            )}

            {selectedType === 'spec' && (
              <>
                <Alert severity="info">
                  既存商品のスペック情報を追加・更新する場合に使用してください。
                </Alert>
                <Alert severity="warning">
                  <strong>spec_id</strong>カラムは変更しないでください。<br />
                  既存商品へのバリエーション追加は<strong>spec_id</strong>を空にすることで追加できます。<br />
                  <strong>product_code</strong>カラムに既存の商品コードを入力してください。
                </Alert>
              </>
            )}

            {selectedType === 'create' && (
              <>
                <Alert severity="info">
                  商品を新規登録する場合に使用してください。<br />
                  <strong>商品CSV</strong> と <strong>バリエーションCSV</strong> の両方が必要です。
                </Alert>
                <Alert severity="warning">
                  <strong>product_code</strong>が一致している必要あります<br />
                </Alert>
              </>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel>対象CSV</InputLabel>
              <Select
                value={selectedType}
                label="対象CSV"
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setCsvFile(null);
                  setVariantFile(null);
                  setMessage('');
                }}
              >
                {csvOptions.map((o) => (
                  <MenuItem key={o.type} value={o.type}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* テンプレDL */}
            <Stack direction="row" gap={1} flexWrap="wrap">
              {csvOptions
                .find(o => o.type === selectedType)
                ?.templatePaths.map((tpl, i) => (
                  <Button
                    key={i}
                    onClick={() => handleDownloadTemplate(tpl.path, tpl.name)}
                    startIcon={<DownloadIcon />}
                    variant="outlined"
                  >
                    {tpl.name}をダウンロード
                  </Button>
                ))}
            </Stack>

            {/* 新規注意 */}
            {selectedType === 'create' && (
              <Typography color="warning.main">
                ※ 新規登録は「商品CSV」と「バリアントCSV」の両方が必要です
              </Typography>
            )}

            {/* 商品CSV */}
            <Stack spacing={1} alignItems="start">
              {/* 商品・バリアント・スペックCSV選択ボタン */}
              <Button variant="contained" component="label" >
                CSVファイルを選択
                <input hidden type="file" accept=".csv" onChange={handleFileChange} />
              </Button>

              {csvFile && (
                <Typography variant="body2">
                  {selectedType === 'product' && `商品CSV: ${csvFile.name}`}
                  {selectedType === 'variant' && `バリアントCSV: ${csvFile.name}`}
                  {selectedType === 'spec' && `スペックCSV: ${csvFile.name}`}
                  {selectedType === 'create' && `商品CSV: ${csvFile.name}`}
                </Typography>
              )}

              {/* 新規一括の場合だけバリアントCSV */}
              {selectedType === 'create' && (
                <>
                  <Button variant="contained" component="label">
                    バリアントCSVを選択
                    <input hidden type="file" accept=".csv" onChange={handleVariantFileChange} />
                  </Button>
                  {variantFile && (
                    <Typography variant="body2">
                      バリアントCSV: {variantFile.name}
                    </Typography>
                  )}
                </>
              )}
            </Stack>

            {/* メッセージ */}
            {message && (
              <Typography
                variant="body2"
                color={message.startsWith('エラー') ? 'error' : 'primary'}
              >
                {message}
              </Typography>
            )}

          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={isUploading}
          >
            {isUploading ? 'アップロード中…' : '実行'}
          </Button>

          <Button
            variant="outlined"
            onClick={handleClose}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CsvUploadButtonProduct;